import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Button, Text, Divider } from 'rsuite';
import dayjs from 'dayjs';
import pluralize from 'pluralize';
import {
  getTaxPaymentPeriodsAsync,
  loadLicensesForPeriod,
} from '../../store/slices/tax/taxThunks';
import { AddAdditionalPeriodsModal } from '../../components/tax/AddAdditionalPeriodsModal';
import {
  selectLicensesPerPeriod,
  selectSelectedPeriodType,
  selectTaxPaymentPeriods,
  selectTaxPaymentPeriodsError,
  selectTaxState,
} from '../../store/slices/tax/taxSelectors';
import Notice from '../../components/user-feedback/Notice';
import {
  clearPreSelectedTaxPeriod,
  resetTaxState,
  setPeriodType,
  updateTaxFlowData,
} from '../../store/slices/tax/taxSlice';
import { FLOWS_ENUM, TAX_PAYMENT_PERIOD_TYPES_ENUM } from '../../common/enums';
import {
  selectBulkTax,
  selectConfigExpressRegistrationTypeVerbiage,
  selectConfigTaxPaymentPeriod,
  selectNumberOfProperties,
} from '../../store/slices/config/configSelectors';
import BasicStyledCheckbox from '../../components/form/BasicStyledCheckbox';
import usePlaceNavigate from '../../hooks/usePlaceNavigate';
import {
  buildMultiLicenseTaxPeriod,
  expandQuarterlyPeriods,
} from 'src/common/taxFlowUtils';
import { RootState, useAppDispatch } from 'src/store/store';
import {
  ExtendedPaymentPeriod,
  LicensesPerPeriod,
  PaymentPeriod,
  PeriodLicense,
  ReportingMethod,
} from '../../types/tax';
import { EndpointData } from '../../types/store';
import { TaxPropertySelection } from '../../components/tax/TaxPropertySelection';
import SegmentedControlButtons from '../../components/common/SegmentedControlButtons';
import FlowLayout from 'src/components/layout/FlowLayout';

const segmentedControlButtonOptions = [
  {
    value: ReportingMethod.INDIVIDUAL,
    label: 'Individually',
  },
  {
    value: ReportingMethod.BULK,
    label: 'Bulk Upload (Spreadsheet)',
  },
];

const taxPeriodButtonOptions = [
  {
    value: TAX_PAYMENT_PERIOD_TYPES_ENUM.MONTHLY,
    label: TAX_PAYMENT_PERIOD_TYPES_ENUM.MONTHLY.toUpperCase(),
  },
  {
    value: TAX_PAYMENT_PERIOD_TYPES_ENUM.QUARTERLY,
    label: TAX_PAYMENT_PERIOD_TYPES_ENUM.QUARTERLY.toUpperCase(),
  },
];

const TaxSelectProperties = () => {
  const dispatch = useAppDispatch();
  const placeNavigate = usePlaceNavigate();

  const [additionalPeriodsModalOpen, setAdditionalPeriodsModalOpen] =
    useState(false);
  const [showNoSelectedLicenseError, setShowNoSelectedLicenseError] =
    useState(false);
  const [hideInactivePermits, setHideInactivePermits] = useState(true);

  // --- config selectors ---
  const isBulkTax = useSelector(selectBulkTax);
  const numberOfProperties = useSelector(selectNumberOfProperties);
  const permitType = useSelector(selectConfigExpressRegistrationTypeVerbiage);
  const taxPaymentPeriod = useSelector(selectConfigTaxPaymentPeriod);

  // --- tax selectors ---
  const { taxFlowData: formData, preSelectedPeriod } =
    useSelector(selectTaxState);
  const allLicensesPerPeriod = useSelector(
    (state: RootState) => state.tax.licensesPerPeriod
  );
  const licensesPerPeriod = useSelector(selectLicensesPerPeriod);
  const visibleLicensesPerPeriod = useMemo(() => {
    if (!hideInactivePermits) return licensesPerPeriod;
    return Object.fromEntries(
      Object.entries(licensesPerPeriod).map(([key, val]) => [
        key,
        { ...val, data: (val.data ?? []).filter((l) => !l.isInactive) },
      ])
    );
  }, [licensesPerPeriod, hideInactivePermits]);
  const paymentPeriods = useSelector(selectTaxPaymentPeriods);
  const paymentPeriodsError = useSelector(selectTaxPaymentPeriodsError);
  const selectedPeriodType = useSelector(selectSelectedPeriodType);

  const [selectedLicenses, setSelectedLicenses] = useState<
    Record<string, Set<string>>
  >({});

  const [periodsToShow, setPeriodsToShow] = useState<
    Array<ExtendedPaymentPeriod>
  >([]);

  // Restart values whenever the user switches the period type (monthly vs quarterly) since the licenses shown are different and we want to avoid confusion
  useEffect(() => {
    setSelectedLicenses({});
    setPeriodsToShow([]);
  }, [selectedPeriodType]);

  useEffect(() => {
    const mostRecent = paymentPeriods.reduce(
      (latest, period) =>
        dayjs(period.startDate).isAfter(dayjs(latest.startDate))
          ? period
          : latest,
      paymentPeriods[0]
    );
    const periods = paymentPeriods.filter(
      (period) =>
        period.startDate === mostRecent?.startDate ||
        preSelectedPeriod?.startDate === period.startDate
    );
    setPeriodsToShow(periods);
    for (const period of periods) {
      dispatch(loadLicensesForPeriod(period));
    }
  }, [dispatch, paymentPeriods, preSelectedPeriod]);

  useEffect(() => {
    if (paymentPeriods.length === 0) {
      dispatch(getTaxPaymentPeriodsAsync());
    }
  }, [dispatch, paymentPeriods.length]);

  useEffect(() => {
    // This is used to set the initial state of the component when the user
    // has gone to the next page and then comes back to this page.
    const initialTrigger = () => {
      if (formData.selectedLicenses.length > 0) {
        setSelectedLicenses(
          formData.selectedLicenses.reduce((prev, period) => {
            return {
              ...prev,
              [period.startDate]: new Set(
                period.licenses.map((license) => license.license)
              ),
            };
          }, {})
        );

        const selectedPeriods = formData.selectedLicenses.map(
          (period) => period.startDate
        );
        setPeriodsToShow(
          paymentPeriods
            .filter((period) => selectedPeriods.includes(period.startDate))
            .sort((a, b) =>
              dayjs(b.startDate).isAfter(dayjs(a.startDate)) ? 1 : -1
            )
        );
      }
    };
    initialTrigger();
  }, [formData, paymentPeriods]);

  const isBulkTaxCapable = useMemo(() => {
    if (!isBulkTax) {
      return false;
    }
    const allLoaded = Object.values(allLicensesPerPeriod).flatMap(
      (bucket: LicensesPerPeriod) =>
        (Object.values(bucket) as EndpointData<PeriodLicense>[]).flatMap(
          (ep) => ep.data
        )
    );
    const licensesAvailable = [...new Set(allLoaded.map((l) => l.license))];
    return licensesAvailable.length >= numberOfProperties;
  }, [numberOfProperties, isBulkTax, allLicensesPerPeriod]);

  const [reportingMethod, setReportingMethod] = useState<ReportingMethod>();

  const handleSetSelectedPeriodType = useCallback(
    (periodType: TAX_PAYMENT_PERIOD_TYPES_ENUM) => {
      dispatch(setPeriodType(periodType));
      dispatch(
        updateTaxFlowData({ selectedLicenses: [], reportingMethod: undefined })
      );
    },
    [dispatch]
  );

  const handleAcceptAdditionalPeriods = useCallback(
    (periods: Array<ExtendedPaymentPeriod>) => {
      periods.forEach((period) => {
        const isNew = !periodsToShow.some(
          (p) => p.startDate === period.startDate
        );
        if (isNew) {
          dispatch(loadLicensesForPeriod(period));
        }
      });
      setPeriodsToShow(
        [...periods].sort((a, b) =>
          dayjs(b.startDate).isAfter(dayjs(a.startDate)) ? 1 : -1
        )
      );
    },
    [dispatch, periodsToShow]
  );

  const toggleSelectedLicenseForPeriod = useCallback(
    (licenseNum: string, period: PaymentPeriod) => {
      const existingLicenses = licensesPerPeriod[period.startDate] ?? null;
      if (existingLicenses) {
        const licenseIndex = existingLicenses.data.findIndex(
          (license) => license.license === licenseNum
        );

        const existingLicense =
          licenseIndex !== -1 ? existingLicenses.data[licenseIndex] : null;
        if (existingLicense) {
          setSelectedLicenses((prev) => {
            const newSelectedLicenses =
              prev[period.startDate] ?? new Set<PeriodLicense>();
            if (prev[period.startDate]?.has(existingLicense.license ?? '')) {
              newSelectedLicenses.delete(existingLicense.license ?? '');
            } else {
              newSelectedLicenses.add(existingLicense.license ?? '');
            }
            return { ...prev, [period.startDate]: newSelectedLicenses };
          });
        }

        setShowNoSelectedLicenseError(false);
      }
    },
    [licensesPerPeriod]
  );

  const toggleSelectAllLicensesForPeriod = useCallback(
    (period: PaymentPeriod, isSelected: boolean) => {
      const existingLicenses =
        visibleLicensesPerPeriod[period.startDate] ?? null;
      if (existingLicenses && isSelected) {
        const selectedLicenses = new Set<string>(
          existingLicenses.data.map((lic) => lic.license ?? '')
        );
        setSelectedLicenses((prev) => ({
          ...prev,
          [period.startDate]: selectedLicenses,
        }));
      } else {
        setSelectedLicenses((prev) => ({
          ...prev,
          [period.startDate]: new Set<string>(),
        }));
      }
    },
    [visibleLicensesPerPeriod]
  );

  const effectiveSelectedLicenses = useMemo(() => {
    if (!hideInactivePermits) return selectedLicenses;
    return Object.fromEntries(
      Object.entries(selectedLicenses).map(([periodKey, licenseSet]) => {
        const visibleIds = new Set(
          (visibleLicensesPerPeriod[periodKey]?.data ?? []).map(
            (l) => l.license ?? ''
          )
        );
        return [
          periodKey,
          new Set([...licenseSet].filter((id) => visibleIds.has(id))),
        ];
      })
    );
  }, [selectedLicenses, hideInactivePermits, visibleLicensesPerPeriod]);

  const hasSelectedLicenses = useMemo(() => {
    return Object.values(effectiveSelectedLicenses).some(
      (licenses) => licenses.size > 0
    );
  }, [effectiveSelectedLicenses]);

  const handleNextClick = useCallback((): Promise<boolean> => {
    const periodsWithSelectedLicenses = Object.keys(
      effectiveSelectedLicenses
    ).filter((key) => effectiveSelectedLicenses[key].size > 0);

    if (periodsWithSelectedLicenses.length > 0) {
      if (!reportingMethod) {
        setShowNoSelectedLicenseError(true);
        return Promise.resolve(false);
      }

      const selectedData = paymentPeriods
        .filter((period) =>
          periodsWithSelectedLicenses.some(
            (periodStart) => periodStart === period.startDate
          )
        )
        .map((period: ExtendedPaymentPeriod) => {
          const licenses = Array.from(
            effectiveSelectedLicenses[period.startDate]
          ).map((licenseId): PeriodLicense | undefined => {
            // Select the license that already has some reporting data in it
            // so that the form in report revenue is filled with the data the user
            // already provided.
            if (formData.selectedLicenses) {
              const formDataPeriod = formData.selectedLicenses.find(
                (selectedPeriod) =>
                  selectedPeriod.startDate === period.startDate
              );
              if (formDataPeriod) {
                const formDataLicense = formDataPeriod.licenses.find(
                  (selectedLicense) => selectedLicense.license === licenseId
                );

                if (formDataLicense) {
                  return formDataLicense;
                }
              }
            }

            return licensesPerPeriod[period.startDate].data.find(
              (license) => license.license === licenseId
            );
          });
          return buildMultiLicenseTaxPeriod(
            period,
            licenses.filter((l): l is PeriodLicense => l !== undefined)
          );
        })
        .sort((a, b) =>
          dayjs(b.startDate).isAfter(dayjs(a.startDate)) ? 1 : -1
        );

      dispatch(clearPreSelectedTaxPeriod());
      const taxLicenses = expandQuarterlyPeriods(
        selectedData,
        selectedPeriodType,
        taxPaymentPeriod
      );
      dispatch(
        updateTaxFlowData({
          selectedLicenses: taxLicenses,
          reportingMethod,
        })
      );
      return Promise.resolve(true);
    }
    setShowNoSelectedLicenseError(true);
    return Promise.resolve(false);
  }, [
    dispatch,
    effectiveSelectedLicenses,
    formData.selectedLicenses,
    licensesPerPeriod,
    paymentPeriods,
    reportingMethod,
    selectedPeriodType,
    taxPaymentPeriod,
  ]);

  const handleBackClick = (): Promise<boolean> => {
    dispatch(resetTaxState());
    placeNavigate('/dashboard');
    return Promise.resolve(true);
  };

  useEffect(() => {
    setReportingMethod(
      isBulkTaxCapable ? formData.reportingMethod : ReportingMethod.INDIVIDUAL
    );
  }, [formData.reportingMethod, isBulkTaxCapable, selectedLicenses]);

  return (
    <FlowLayout
      flow={FLOWS_ENUM.PAY_TOT}
      onBack={handleBackClick}
      onNext={handleNextClick}
      omitNavigationForward={!reportingMethod || !hasSelectedLicenses}
      showBack
      showNext
      title="Select Properties for Tax Reporting"
    >
      <Text className="mb-24">
        Your properties that are registered for tax reporting and remittance in
        the selected period are listed below. Please select the properties for
        which you wish to report at this time.
      </Text>
      {taxPaymentPeriod === TAX_PAYMENT_PERIOD_TYPES_ENUM.MONTHLY_QUARTERLY && (
        <div style={{ margin: '16px 0' }}>
          <SegmentedControlButtons
            options={taxPeriodButtonOptions}
            setValue={handleSetSelectedPeriodType}
            value={selectedPeriodType}
          />
        </div>
      )}
      <TaxPropertySelection
        licensesPerPeriod={visibleLicensesPerPeriod}
        periodsToShow={periodsToShow}
        permitType={permitType}
        selectAll={toggleSelectAllLicensesForPeriod}
        selectLicense={toggleSelectedLicenseForPeriod}
        selectedLicenses={selectedLicenses}
      />
      {paymentPeriodsError && (
        <Notice
          style={{ marginBottom: '15px' }}
          message={paymentPeriodsError}
        />
      )}
      <div className="responsive-stack">
        <Button
          className="btn-secondary sm-100-percent"
          disabled={paymentPeriods.length === 0}
          onClick={() => setAdditionalPeriodsModalOpen(true)}
        >
          Add Additional Periods
        </Button>

        <BasicStyledCheckbox
          onChange={(checked: boolean) => setHideInactivePermits(checked)}
          checked={hideInactivePermits}
          label={`Hide Inactive ${pluralize(permitType)}`}
        />
      </div>
      {isBulkTaxCapable && (
        <>
          <Divider />
          <Text className="text-large text-bold mb-12">
            How would you like to report tax for the selected properties?
          </Text>
          <Text className="mb-16">
            For tax reporting of {numberOfProperties} or more properties, you
            can report individually, or upload in bulk using a spreadsheet
            template on the next screen.
          </Text>
          <SegmentedControlButtons
            value={reportingMethod}
            setValue={setReportingMethod}
            options={segmentedControlButtonOptions}
          />
        </>
      )}
      {showNoSelectedLicenseError && (
        <Notice
          message={
            !reportingMethod
              ? 'Please select a reporting method to continue'
              : 'Please select one or more licenses to continue'
          }
          className="mt-12"
        />
      )}
      <AddAdditionalPeriodsModal
        availableReportingPeriods={paymentPeriods}
        isOpen={additionalPeriodsModalOpen}
        onAccept={handleAcceptAdditionalPeriods}
        selectedPeriods={periodsToShow}
        setOpen={setAdditionalPeriodsModalOpen}
        taxPaymentPeriod={taxPaymentPeriod}
      />
    </FlowLayout>
  );
};

export default TaxSelectProperties;
